import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { PORTAL_NAME } from '@kioskgaming/ui';
import { useCashierAuth } from '../hooks/useCashierAuth';
import { cashierPairDevice, DeviceView } from '../services/cashierApi';
import { DeviceErrorInfo, describeDeviceError } from '../utils/deviceErrors';

export const PAIRING_CODE_LENGTH = 8;

/** "k7qm-3xpd" / "K7QM 3XPD" → "K7QM3XPD" (upper-case, letters and digits only, max 8). */
export function normalizePairingCode(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, PAIRING_CODE_LENGTH);
}

/** "K7QM3XPD" → "K7QM-3XPD" (how the Shop portal shows it). */
export function formatPairingCode(normalized: string): string {
  return normalized.length > 4 ? `${normalized.slice(0, 4)}-${normalized.slice(4)}` : normalized;
}

type PairingCodeFormProps = {
  /** Called after the server approved this device with the code. */
  onPaired?: (device: DeviceView) => void;
};

export default function PairingCodeForm({ onPaired }: PairingCodeFormProps) {
  const { setDevice, refreshMe, logout } = useCashierAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<DeviceErrorInfo | null>(null);

  const complete = code.length === PAIRING_CODE_LENGTH;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!complete) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await cashierPairDevice(code);
      if (res.success && res.data?.device) {
        setDevice(res.data.device);
        setCode('');
        toast.success(res.data.alreadyApproved ? 'This device is already approved' : 'Device approved');
        onPaired?.(res.data.device);
        return;
      }
      const info = describeDeviceError(res.code, { serverMessage: res.message }) || {
        title: 'Pairing failed',
        message: res.message || 'Pairing failed. Try again.',
        action: 'retry' as const,
      };
      setError(info);
      toast.error(info.message);
      // The device was blocked meanwhile: refresh so the screen shows the blocked state.
      if (res.code === 'DEVICE_BLOCKED') void refreshMe();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={(e) => void onSubmit(e)} className="rounded-lg border border-stone-200 bg-white p-3">
      <label htmlFor="pairing-code" className="block text-sm font-semibold text-stone-900">
        Have a pairing code?
      </label>
      <p className="mt-0.5 text-xs text-stone-500">Your manager can create one in the {PORTAL_NAME.shop} (Cashiers → Devices).</p>
      <div className="mt-2 flex gap-2">
        <input
          id="pairing-code"
          value={formatPairingCode(code)}
          onChange={(e) => {
            setCode(normalizePairingCode(e.target.value));
            setError(null);
          }}
          className="w-full min-w-0 rounded-lg border border-stone-200 px-3 py-2 font-mono text-base uppercase tracking-widest outline-none focus:border-emerald-500"
          placeholder="XXXX-XXXX"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          disabled={submitting}
        />
        <button
          type="submit"
          disabled={!complete || submitting}
          className="shrink-0 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
        >
          {submitting ? 'Checking…' : 'Pair device'}
        </button>
      </div>
      {error ? (
        <div role="alert" className="mt-2 text-sm text-rose-700">
          <span className="font-semibold">{error.title}.</span> {error.message}
          {error.action === 'sign_in_again' ? (
            <button
              type="button"
              onClick={() => {
                logout();
                navigate('/login', { replace: true });
              }}
              className="mt-2 block rounded-lg border border-rose-200 bg-white px-3 py-1.5 text-sm font-semibold text-rose-700 hover:bg-rose-50"
            >
              Sign in again
            </button>
          ) : null}
        </div>
      ) : null}
    </form>
  );
}
